import { HistoricalChangeEntry } from "../@types/history";
import { JobV2, JobClient, JobLineItem } from "../@types/job";
import { StoredPart } from "../@types/location";
import { LineItemMaterial } from "../@types/expense";
import {
  isSpecimenLineItem,
  isServiceLineItem,
  isProcessingFeeLineItem,
  mountLinkLabel,
} from "../_helpers/lineItem";
import { formatCamelCaseString } from "../_helpers/string";

// Import Measurements type from specimen
type Measurements = Record<string, number>;

// Different save paths represent "empty" differently (undefined vs null vs ""),
// which used to record phantom history entries like '' -> null
const isEmptyValue = (value: unknown) =>
  value === undefined || value === null || value === "";

const valuesDiffer = (before: unknown, after: unknown) => {
  if (isEmptyValue(before) && isEmptyValue(after)) return false;
  return JSON.stringify(before) !== JSON.stringify(after);
};

export const getJobChanges = (
  currentJob: JobV2,
  updatedJob: JobV2
): HistoricalChangeEntry[] => {
  const changes: HistoricalChangeEntry[] = [];

  Object.keys(updatedJob).forEach((key) => {
    if (
      key === "uid" ||
      key === "createdAt" ||
      key === "updatedAt" ||
      key === "history" ||
      key === "payments" ||
      key === "communications" ||
      key === "lastWriteTabId" ||
      key.toLowerCase().includes("processing fee")
    )
      return;

    let currentValue = currentJob[key as keyof JobV2];
    let newValue = updatedJob[key as keyof JobV2];

    if (key === "client") {
      // Handle client changes
      const possibleCurrentClient = currentValue as JobClient;
      const possibleNewClient = newValue as JobClient;
      if (possibleCurrentClient?.uid !== possibleNewClient?.uid) {
        changes.push({
          field: key,
          before: possibleCurrentClient || null,
          after: possibleNewClient || null,
        });
      }
    } else if (key === "lineItems") {
      // Handle changes in lineItems array
      const currentLineItems = currentValue as JobLineItem[];
      const newLineItems = newValue as JobLineItem[];

      // Record the linked mount by name and mount id, not raw uid
      const resolveMountName = (uid: unknown) => {
        if (!uid) return null;
        const mount = [...newLineItems, ...currentLineItems]
          .filter(isSpecimenLineItem)
          .find((m) => m.uid === uid);
        if (!mount) return "Unknown Mount";
        return mountLinkLabel(mount);
      };

      // Added line items (processing fees are auto-managed by payments, skip)
      newLineItems.forEach((newItem) => {
        if (currentLineItems.some((item) => item.uid === newItem.uid)) return;
        if (isProcessingFeeLineItem(newItem)) return;
        if (isSpecimenLineItem(newItem)) {
          changes.push({
            field: "Mount Added",
            before: null,
            after: newItem.specimen.species || "New Mount",
          });
        } else if (isServiceLineItem(newItem)) {
          const linkedName = resolveMountName(newItem.linkedSpecimenUid);
          changes.push({
            field: "Service Added",
            before: null,
            after: linkedName
              ? `${newItem.service} (linked to ${linkedName})`
              : newItem.service,
          });
        }
      });

      // Removed line items
      currentLineItems.forEach((currentItem) => {
        if (newLineItems.some((item) => item.uid === currentItem.uid)) return;
        if (isProcessingFeeLineItem(currentItem)) return;
        changes.push({
          field: isSpecimenLineItem(currentItem)
            ? "Mount Removed"
            : "Service Removed",
          before: isSpecimenLineItem(currentItem)
            ? currentItem.specimen.species || "New Mount"
            : currentItem.service,
          after: null,
        });
      });

      newLineItems.forEach((newItem) => {
        const currentItem = currentLineItems.find(
          (item) => item.uid === newItem.uid
        );
        if (!currentItem) return; // Skip if there's no corresponding current item

        Object.keys(newItem).forEach((itemKey) => {
          if (itemKey === "updatedAt") return; // Skip updatedAt field
          const currentItemValue = currentItem[itemKey as keyof JobLineItem];
          const newItemValue = newItem[itemKey as keyof JobLineItem];

          if (
            itemKey === "specimen" &&
            isSpecimenLineItem(newItem) &&
            isSpecimenLineItem(currentItem)
          ) {
            const currentSpecimen = currentItem.specimen;
            const newSpecimen = newItem.specimen;

            Object.keys(newSpecimen).forEach((specimenKey) => {
              const currentSpecimenValue =
                currentSpecimen[specimenKey as keyof typeof currentSpecimen];
              const newSpecimenValue =
                newSpecimen[specimenKey as keyof typeof newSpecimen];

              if (specimenKey === "measurements") {
                const currentMeasurements =
                  currentSpecimenValue as Measurements | null;
                const newMeasurements = newSpecimenValue as Measurements | null;

                compareMeasurements(
                  currentMeasurements,
                  newMeasurements,
                  newSpecimen.species!,
                  changes
                );
              } else if (specimenKey === "parts") {
                const currentParts = currentSpecimenValue as StoredPart[];
                const newParts = newSpecimenValue as StoredPart[];

                compareParts(
                  currentParts,
                  newParts,
                  newSpecimen.species!,
                  changes
                );
              } else if (valuesDiffer(currentSpecimenValue, newSpecimenValue)) {
                changes.push({
                  field: `${newSpecimen.species} ${formatCamelCaseString(
                    specimenKey
                  )}`,
                  before:
                    currentSpecimenValue === undefined
                      ? null
                      : currentSpecimenValue,
                  after:
                    newSpecimenValue === undefined ? null : newSpecimenValue,
                });
              }
            });
          } else if (itemKey === "materials") {
            compareMaterials(
              (currentItemValue as LineItemMaterial[] | undefined) ?? [],
              (newItemValue as LineItemMaterial[] | undefined) ?? [],
              isSpecimenLineItem(newItem)
                ? newItem.specimen.species || "Unknown"
                : newItem.service || "Unknown",
              changes
            );
          } else if (
            itemKey === "linkedSpecimenUid" &&
            isServiceLineItem(newItem)
          ) {
            if (valuesDiffer(currentItemValue, newItemValue)) {
              changes.push({
                field: `${newItem.service} Linked Mount`,
                before: resolveMountName(currentItemValue),
                after: resolveMountName(newItemValue),
              });
            }
          } else if (valuesDiffer(currentItemValue, newItemValue)) {
            changes.push({
              field: `${
                isSpecimenLineItem(newItem)
                  ? newItem.specimen.species
                  : newItem.service
              } ${formatCamelCaseString(itemKey)}`,
              before: currentItemValue === undefined ? null : currentItemValue,
              after: newItemValue === undefined ? null : newItemValue,
            });
          }
        });
      });
    } else if (valuesDiffer(currentValue, newValue)) {
      changes.push({
        field: key,
        before: currentValue === undefined ? null : currentValue,
        after: newValue === undefined ? null : newValue,
      });
    }
  });

  return changes;
};

function compareMeasurements(
  currentMeasurements: Measurements | null,
  newMeasurements: Measurements | null,
  species: string,
  changes: HistoricalChangeEntry[]
) {
  if (currentMeasurements && !newMeasurements) {
    // Measurements were present and then removed entirely
    changes.push({
      field: `${species} Measurements`,
      before: null,
      after: "Measurements Removed",
    });
    return;
  }

  const allKeys = new Set([
    ...(currentMeasurements ? Object.keys(currentMeasurements) : []),
    ...(newMeasurements ? Object.keys(newMeasurements) : []),
  ]);

  allKeys.forEach((measurementKey) => {
    const currentValue = currentMeasurements?.[measurementKey] ?? null;
    const newValue = newMeasurements?.[measurementKey] ?? null;

    if (currentValue !== newValue) {
      changes.push({
        field: `${species} Measurement: ${measurementKey}`,
        before: currentValue,
        after: newValue,
      });
    }
  });
}

function compareParts(
  currentParts: StoredPart[],
  newParts: StoredPart[],
  species: string,
  changes: HistoricalChangeEntry[]
) {
  // Check for created parts (in new but not in current)
  newParts.forEach((newPart) => {
    const currentPart = currentParts.find((part) => part.uid === newPart.uid);
    if (!currentPart) {
      changes.push({
        field: `${species} Part`,
        before: null,
        after: `${newPart.name} - ${newPart.locationCode}`,
      });
    }
  });

  // Check for deleted parts (in current but not in new)
  currentParts.forEach((currentPart) => {
    const newPart = newParts.find((part) => part.uid === currentPart.uid);
    if (!newPart) {
      changes.push({
        field: `${species} Part`,
        before: `${currentPart.name} - ${currentPart.locationCode}`,
        after: null,
      });
    }
  });

  // Check for updated parts (name or location changed)
  currentParts.forEach((currentPart) => {
    const newPart = newParts.find((part) => part.uid === currentPart.uid);
    if (newPart) {
      // Check if name changed
      if (currentPart.name !== newPart.name) {
        changes.push({
          field: `${species} Part Name`,
          before: `${currentPart.name} - ${currentPart.locationCode}`,
          after: `${newPart.name} - ${newPart.locationCode}`,
        });
      }

      // Check if location changed
      if (currentPart.locationCode !== newPart.locationCode) {
        changes.push({
          field: `${species} Part Location`,
          before: `${currentPart.name} - ${currentPart.locationCode}`,
          after: `${newPart.name} - ${newPart.locationCode}`,
        });
      }
    }
  });
}

function compareMaterials(
  currentMaterials: LineItemMaterial[],
  newMaterials: LineItemMaterial[],
  species: string,
  changes: HistoricalChangeEntry[]
) {
  // Check for added materials
  newMaterials.forEach((newMat) => {
    const currentMat = currentMaterials.find((m) => m.uid === newMat.uid);
    if (!currentMat) {
      changes.push({
        field: `${species} Material`,
        before: null,
        after: `${newMat.description} x${newMat.quantity}`,
      });
    }
  });

  // Check for removed materials
  currentMaterials.forEach((currentMat) => {
    const newMat = newMaterials.find((m) => m.uid === currentMat.uid);
    if (!newMat) {
      changes.push({
        field: `${species} Material`,
        before: `${currentMat.description} x${currentMat.quantity}`,
        after: null,
      });
    }
  });

  // Check for quantity changes
  currentMaterials.forEach((currentMat) => {
    const newMat = newMaterials.find((m) => m.uid === currentMat.uid);
    if (newMat && currentMat.quantity !== newMat.quantity) {
      changes.push({
        field: `${species} Material: ${newMat.description}`,
        before: currentMat.quantity,
        after: newMat.quantity,
      });
    }
  });
}
